import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Slider,
  Grid,
  Card,
  CardContent,
  Menu,
  MenuItem,
  Select,
  FormControl,
  Checkbox,
  FormControlLabel,
  Stack,
  Pagination,
  Typography,
  Breadcrumbs,
  Link as MUILink,
  Divider,
  Switch,
  Chip,
 ToggleButton, 
 ToggleButtonGroup 
} from "@mui/material";
import {
  Settings,
  CreditCard,
  CalendarToday,
  Close,
  Delete,
  MoreVert,
} from "@mui/icons-material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getAllBuyerCategories,
  createBuyerCategory,
  deleteBuyerCategory,
  updateBuyerCategory,
} from "../redux/slices/buyerCategoriesSlice";
import { getPaymentOptionByUser } from "../redux/slices/paymentOptionSlice";
import EditBuyerCategoryDialog from "../Components/EditBuyerCategoryDialog";
import { useNavigate, Link } from "react-router-dom";
import PageTitle from "../Components/PageTitle";

const CategoryManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const {
    categories: buyerCategories,
    loading,
    page,
    total,
    totalPages,
    limit,
  } = useSelector((state) => state.buyerCategories);
  const {
    items: paymentOptions,
    loading: optionsLoading,
    optionsPagination,
  } = useSelector((state) => state.paymentOptions);

  const [openCreateTier, setOpenCreateTier] = useState(false);
  // Form state variables
  const [tierDiscount, setTierDiscount] = useState(0);
  const [tierName, setTierName] = useState("");
  const [tierColor, setTierColor] = useState("purple");
  // Payment options state
  const [includeCash, setIncludeCash] = useState(false);
  const [includeCredit, setIncludeCredit] = useState(false);
  const [cashDiscount, setCashDiscount] = useState("");
  const [creditPeriod, setCreditPeriod] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [creditLimit, setCreditLimit] = useState("");
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCategoryForMenu, setSelectedCategoryForMenu] = useState(null);

const [showKioskOnly, setShowKioskOnly] = useState(false); // remove 
const [categoryFilter, setCategoryFilter] = useState("all"); 
// default = all

  // Reset form function
  const resetForm = () => {
    setTierName("");
    setTierColor("purple");
    setTierDiscount(0);
    setIncludeCash(false);
    setIncludeCredit(false);
    setCashDiscount("");
    setCreditPeriod("");
    setInterestRate("");
    setCreditLimit("");
  };

  useEffect(() => {
    if (user?._id) {
      dispatch(getAllBuyerCategories({ page: page || 1 }));
      dispatch(
        getPaymentOptionByUser({
          userId: user._id,
        }),
      );
    }
  }, [dispatch, page, user?._id]);

  // Page Change Handler
  const handlePageChange = (event, value) => {
    dispatch(getAllBuyerCategories({ page: value }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle form submission
  const handleSaveCategory = () => {
    // Validation
    if (!tierName.trim()) {
      toast.error("Please enter buyer category name");
      return;
    }

    // Prepare payment options
    const paymentOptions = [];

    if (includeCash) {
      // if (
      //   !cashDiscount ||
      //   isNaN(cashDiscount) ||
      //   parseFloat(cashDiscount) < 0
      // ) {
      //   toast.error("Please enter a valid cash discount percentage");
      //   return;
      // }
      paymentOptions.push({
        type: "Cash",
        cashDiscount: parseFloat(cashDiscount),
      });
    }

    if (includeCredit) {
      // if (!creditPeriod || isNaN(creditPeriod) || parseInt(creditPeriod) <= 0) {
      //   toast.error("Please enter a valid credit period");
      //   return;
      // }
      // if (
      //   !interestRate ||
      //   isNaN(interestRate) ||
      //   parseFloat(interestRate) < 0
      // ) {
      //   toast.error("Please enter a valid interest rate");
      //   return;
      // }
      // if (!creditLimit || isNaN(creditLimit) || parseFloat(creditLimit) <= 0) {
      //   toast.error("Please enter a valid credit limit");
      //   return;
      // }

      paymentOptions.push({
        type: "Credit",
        creditPeriod: parseInt(creditPeriod),
        interestRate: parseFloat(interestRate),
        creditLimit: parseFloat(creditLimit),
      });
    }

    // Check if at least one payment option is selected
    // if (paymentOptions.length === 0) {
    //   toast.error("Please select at least one payment option (Cash or Credit)");
    //   return;
    // }

    // Prepare category data
    const categoryData = {
      name: tierName.trim(),
      color: tierColor,
      discount: tierDiscount,
      paymentOptions: paymentOptions,
    };

    // Dispatch create action
    dispatch(createBuyerCategory(categoryData))
      .unwrap()
      .then(() => {
        toast.success("Buyer category created successfully!");
        resetForm();
        setOpenCreateTier(false);
        // Refresh categories list
        dispatch(getAllBuyerCategories({ page, limit }));
        dispatch(getPaymentOptionByUser({ userId: user._id, page, limit}));
      })
      .catch((error) => {
        toast.error(error.message || "Failed to create buyer category");
      });
  };

  // Handle dialog close
  const handleDialogClose = () => {
    resetForm();
    setOpenCreateTier(false);
  };

  // Handle edit button click
  const handleEditClick = (category) => {
    // Find the corresponding payment option for this category
    const paymentOption = paymentOptions.find(
      (option) =>
        option?.buyerCategory &&
        (option?.buyerCategory?._id === category?._id ||
          (typeof option?.buyerCategory === "string" &&
            option?.buyerCategory === category?._id)),
    );

    setSelectedCategory(category);
    setSelectedPaymentOption(paymentOption);
    setOpenEditDialog(true);
  };

  // Handle update category
  const handleUpdateCategory = (updateData) => {
    dispatch(updateBuyerCategory(updateData))
      .unwrap()
      .then(() => {
        toast.success("Buyer category updated successfully!");
        setOpenEditDialog(false);
        setSelectedCategory(null);
        setSelectedPaymentOption(null);
        // Refresh categories list
        dispatch(getAllBuyerCategories({ page, limit }));
        dispatch(getPaymentOptionByUser({userId: user._id, page, limit}));
      })
      .catch((error) => {
        toast.error(error.message || "Failed to update buyer category");
      });
  };

  // Handle delete button click
  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setOpenDeleteDialog(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = () => {
    if (!categoryToDelete) return;

    dispatch(deleteBuyerCategory(categoryToDelete._id))
      .unwrap()
      .then(() => {
        toast.success("Buyer category deleted successfully!");
        setOpenDeleteDialog(false);
        setCategoryToDelete(null);
        // Refresh categories list
        dispatch(getAllBuyerCategories(page, limit));
        dispatch(getPaymentOptionByUser({userId: user._id, page, limit}));
      })
      .catch((error) => {
        toast.error(error.message || "Failed to delete buyer category");
        setOpenDeleteDialog(false);
        setCategoryToDelete(null);
      });
  };

  // Handle delete dialog close
  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
    setCategoryToDelete(null);
  };

  // Handle menu open
  const handleMenuOpen = (event, category) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategoryForMenu(category);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCategoryForMenu(null);
  };

  // Handle edit from menu
  const handleEditFromMenu = () => {
    if (selectedCategoryForMenu) {
      handleEditClick(selectedCategoryForMenu);
      handleMenuClose();
    }
  };

  // Handle delete from menu
  const handleDeleteFromMenu = () => {
    if (selectedCategoryForMenu) {
      handleDeleteClick(selectedCategoryForMenu);
      handleMenuClose();
    }
  };

  const filteredCategories = buyerCategories.filter((category) => {
    const name = category?.name?.toLowerCase();

    if (showKioskOnly) {
      return name === "kiosk" || name === "kiosk (consumer)";
    }

    return true; 
  });

//   const filteredCategories = buyerCategories?.filter((category) => {
//   const name = category?.name?.toLowerCase();

//   const isKiosk =
//     name === "kiosk" || name === "kiosk (consumer)";

//   if (categoryFilter === "kiosk") {
//     return isKiosk; // Only kiosk
//   }

//   // Default = All → kiosk hide
//   return !isKiosk;
// });


  const getCategoryDisplayName = (name) => {
    if (!name) return "";

    const lower = name.toLowerCase();

    if (lower === "kiosk")
      // return "Kiosk (Consumer)";

      return name;
  };

  return (
    <>
      <PageTitle
        title="Buyer Categories - BizBridge"
        description="Manage your buyer categories and payment options"
        keywords="buyer categories, payment options, category management"
        url="/seller-dashboard/buyer-categories"
      />
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          background: "#f8fafc",
          py: { xs: 1, sm: 2 },
        }}
      >
        {/* Breadcrumb */}
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
            width: "fit-content",
            backgroundColor: "#fff",
            ml: { xs: 1, sm: 2.9 },
            padding: "13px",
            borderRadius: "12px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
        >
          <MUILink
            component={Link}
            to="/seller-dashboard"
            sx={{ color: "inherit", textDecoration: "none" }}
          >
            Dashboard
          </MUILink>
          <Typography sx={{ color: "#0b4064", fontWeight: "bold" }}>
            Buyer Category
          </Typography>
        </Breadcrumbs>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ maxWidth: 1350, mx: "auto", px: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 3,
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <Typography fontWeight={700} fontSize={22}>
              Buyer Categories
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* Kiosk Filter Switch */}
              {/* <FormControlLabel
                control={
                  <Switch
                    checked={showKioskOnly}
                    onChange={(e) => setShowKioskOnly(e.target.checked)}
                    size="small"
                  />
                }
                label="Kiosk only"
                sx={{
                  fontSize: 14,
                  userSelect: "none",
                }}
              /> */}


{/* <ToggleButtonGroup
  value={categoryFilter}
  exclusive
  onChange={(e, newValue) => {
    if (newValue !== null) {
      setCategoryFilter(newValue);
    }
  }}
  size="small"
  sx={{
    height: 36,
    "& .MuiToggleButton-root": {
      textTransform: "none",
      fontWeight: 600,
      px: 2,
    },
  }}
>
  <ToggleButton value="all">All</ToggleButton>
  <ToggleButton value="kiosk">Kiosk</ToggleButton>
</ToggleButtonGroup> */}

              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setOpenCreateTier(true)}
                sx={{
                  textTransform: "none",
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  fontSize: 15,
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#0b4064",
                  },
                }}
              >
                Add Category
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {filteredCategories?.map((category, index) => {
              // Find the corresponding payment option for this category
              const paymentOption = paymentOptions?.find(
                (option) =>
                  option?.buyerCategory &&
                  (option?.buyerCategory?._id === category?._id ||
                    (typeof option?.buyerCategory === "string" &&
                      option?.buyerCategory === category?._id)),
              );

              // Get color for the category
              const getColorDetails = (color) => {
                const colorMap = {
                  purple: {
                    bg: "#f3e5f5",
                    text: "#9c27b0",
                    primary: "#9c27b0",
                  },
                  blue: { bg: "#e3f2fd", text: "#2196f3", primary: "#2196f3" },
                  green: { bg: "#e8f5e9", text: "#4caf50", primary: "#4caf50" },
                  orange: {
                    bg: "#fff3e0",
                    text: "#ff9800",
                    primary: "#ff9800",
                  },
                  red: { bg: "#ffebee", text: "#f44336", primary: "#f44336" },
                  default: {
                    bg: "#f5f5f5",
                    text: "#666666",
                    primary: "#666666",
                  },
                };
                return colorMap[color] || colorMap.default;
              };

              const colorDetails = getColorDetails(category?.color);

              // Get the first letter of category name for the avatar
              const firstLetter = category?.name?.charAt(0)?.toUpperCase();

              // Get payment details
              const cashDiscount =
                paymentOption?.cashPayment?.discountPercent || 0;
              const creditLimit =
                paymentOption?.creditPayment?.creditLimit || 0;
              const creditPeriod =
                paymentOption?.creditPayment?.creditPeriodDays || 0;

              // Format credit limit
              const formatCreditLimit = (limit) => {
                if (limit >= 1000000)
                  return `₹${(limit / 1000000).toFixed(1)}M`;
                if (limit >= 1000) return `₹${(limit / 1000).toFixed(0)}k`;
                return `₹${limit}`;
              };

              // Get payment terms text
              const getPaymentTerms = () => {
                if (!paymentOption) return "Advance";
                if (paymentOption.paymentType === "Cash") return "Cash";
                if (paymentOption.paymentType === "Credit") {
                  return creditPeriod > 0 ? `Net ${creditPeriod}` : "Advance";
                }
                if (paymentOption.paymentType === "Both") {
                  return cashDiscount > 0 && creditPeriod > 0
                    ? `Cash/${creditPeriod} days`
                    : "Advance";
                }
                return "Advance";
              };

              return (
                <Grid item xs={12} sm={12} md={6} lg={4} key={category?._id}>
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                        border: "1px solid #e5e7eb",
                        backgroundColor: "#ffffff",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 3,
                          alignItems: "flex-start",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              backgroundColor: colorDetails.bg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 20,
                              fontWeight: 700,
                              color: colorDetails.text,
                            }}
                          >
                            {firstLetter}
                          </Box>

                          <Box>
                            {/* <h3
                              style={{
                                margin: 0,
                                fontSize: 18,
                                fontWeight: 600,
                                color: "#1a1a1a",
                              }}
                            >
                              {category.name}
                              {getCategoryDisplayName(category.name)}
                            </h3> */}

                            <h3
                              style={{
                                margin: 0,
                                fontSize: 18,
                                fontWeight: 600,
                                color: "#1a1a1a",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              {category.name}
                              {/* {getCategoryDisplayName(category.name)} */}

                              {category.name?.toLowerCase() === "kiosk" && (
                                <Chip
                                  label="Consumer"
                                  size="small"
                                  color="primary"
                                  sx={{
                                    fontSize: 11,
                                    height: 20,
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </h3>
                          </Box>
                        </Box>

                        <IconButton
                          size="small"
                          sx={{ color: "#9ca3af" }}
                          onClick={(e) => handleMenuOpen(e, category)}
                          title="More Actions"
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>

                        {/* Menu for actions */}
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={handleMenuClose}
                          PaperProps={{
                            sx: {
                              borderRadius: 2,
                              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                              minWidth: 180,
                            },
                          }}
                        >
                          <MenuItem
                            onClick={handleEditFromMenu}
                            sx={{
                              fontSize: 14,
                              fontWeight: 500,
                              py: 1.5,
                              "&:hover": {
                                backgroundColor: "#f3f4f6",
                              },
                            }}
                          >
                            <Settings
                              sx={{ fontSize: 18, mr: 1.5, color: "#6b7280" }}
                            />
                            Edit Category
                          </MenuItem>
                          <MenuItem
                            onClick={handleDeleteFromMenu}
                            sx={{
                              fontSize: 14,
                              fontWeight: 500,
                              py: 1.5,
                              color: "#ef4444",
                              "&:hover": {
                                backgroundColor: "#fee2e2",
                              },
                            }}
                          >
                            <Delete
                              sx={{ fontSize: 18, mr: 1.5, color: "#ef4444" }}
                            />
                            Delete Category
                          </MenuItem>
                        </Menu>
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1.5,
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 15,
                              color: "#6b7280",
                              fontWeight: 600,
                              letterSpacing: 0.5,
                              textTransform: "uppercase",
                            }}
                          >
                            GLOBAL DISCOUNT
                          </span>
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: colorDetails.primary,
                            }}
                          >
                            {category.discount}% OFF
                          </span>
                        </Box>
                        <Box
                          sx={{
                            height: 8,
                            backgroundColor: "#e5e7eb",
                            borderRadius: 1,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${Math.min(category.discount, 100)}%`,
                              backgroundColor: colorDetails.primary,
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                        <Box
                          sx={{
                            flex: 1,
                            backgroundColor: "#f9fafb",
                            p: 2,
                            borderRadius: 2,
                            textAlign: "center",
                          }}
                        >
                          <CreditCard
                            sx={{ fontSize: 18, color: "#9ca3af", mb: 0.5 }}
                          />
                          <div
                            style={{
                              fontSize: 10,
                              color: "#6b7280",
                              fontWeight: 600,
                              letterSpacing: 0.5,
                              textTransform: "uppercase",
                              marginTop: 8,
                            }}
                          >
                            LIMIT
                          </div>
                          <div
                            style={{
                              fontSize: 15,
                              fontWeight: 700,
                              marginTop: 4,
                              color: "#1a1a1a",
                            }}
                          >
                            {formatCreditLimit(creditLimit)}
                          </div>
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            backgroundColor: "#f9fafb",
                            p: 2,
                            borderRadius: 2,
                            textAlign: "center",
                          }}
                        >
                          <CalendarToday
                            sx={{ fontSize: 18, color: "#9ca3af", mb: 0.5 }}
                          />
                          <div
                            style={{
                              fontSize: 10,
                              color: "#6b7280",
                              fontWeight: 600,
                              letterSpacing: 0.5,
                              textTransform: "uppercase",
                              marginTop: 8,
                            }}
                          >
                            TERMS
                          </div>
                          <div
                            style={{
                              fontSize: 15,
                              fontWeight: 700,
                              marginTop: 4,
                              color: "#1a1a1a",
                            }}
                          >
                            {getPaymentTerms()}
                          </div>
                        </Box>
                      </Box>

                      {/* Additional payment info if needed */}
                      {paymentOption && (
                        <Box sx={{ mb: 2 }}>
                          {cashDiscount > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                              }}
                            >
                              <span style={{ fontSize: 12, color: "#6b7280" }}>
                                Cash Discount:
                              </span>
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: colorDetails.primary,
                                }}
                              >
                                {cashDiscount}%
                              </span>
                            </Box>
                          )}
                          {paymentOption.creditPayment?.interestRatePerYear >
                            0 && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                              }}
                            >
                              <span style={{ fontSize: 12, color: "#6b7280" }}>
                                Interest Rate:
                              </span>
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: colorDetails.primary,
                                }}
                              >
                                {
                                  paymentOption.creditPayment
                                    .interestRatePerYear
                                }
                                %
                              </span>
                            </Box>
                          )}
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          pt: 2,
                          borderTop: "1px solid #e5e7eb",
                          mt: "auto",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            color: "#6b7280",
                            fontWeight: 600,
                          }}
                        >
                          {firstLetter}
                        </span>
                        <Button
                          variant="text"
                          onClick={() => {
                            navigate("/seller-dashboard/buyer-view-members", {
                              state: {
                                buyerCategoryId: category._id,
                                buyerCategoryName: category.name,
                                buyerCategoryColor: category.color,
                              },
                            });
                          }}
                          sx={{
                            textTransform: "none",
                            color: "#0b4064",
                            fontWeight: 600,
                            fontSize: 13,
                            p: 0,
                            minWidth: "auto",
                            "&:hover": {
                              backgroundColor: "transparent",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          View Members
                        </Button>
                      </Box>
                    </Paper>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
          {/* 5. Pagination UI */}
          {totalPages > 1 && (
            <Stack spacing={2} alignItems="center" sx={{ mt: 5, mb: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                shape="rounded"
                showFirstButton
                showLastButton
              />
              <Typography variant="caption" color="textSecondary">
                Showing page {page} of {totalPages}
              </Typography>
            </Stack>
          )}
        </Box>

        {/* ---------------- Create New Buyer Category Dialog ---------------- */}
        <Dialog
          open={openCreateTier}
          onClose={handleDialogClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, maxWidth: 1000, width: "100%" },
          }}
        >
          <DialogTitle
            sx={{ fontSize: 28, fontWeight: 700, pb: 3, pt: 4, px: 4 }}
          >
            Create New Buyer Category
          </DialogTitle>
          <IconButton
            onClick={handleDialogClose}
            sx={{ position: "absolute", right: 24, top: 24, color: "#9ca3af" }}
          >
            <Close />
          </IconButton>

          <DialogContent sx={{ px: 4, pb: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Box sx={{ mb: 1 }}>
                  <label
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#6b7280",
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    Buyer Category Name
                  </label>
                </Box>
                <TextField
                  fullWidth
                  value={tierName}
                  onChange={(e) => setTierName(e.target.value)}
                  placeholder="Enter category name"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "#f9fafb",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ mb: 1 }}>
                  <label
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#6b7280",
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    COLOR
                  </label>
                </Box>
                <FormControl fullWidth>
                  <Select
                    value={tierColor}
                    onChange={(e) => setTierColor(e.target.value)}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: "#f9fafb",
                    }}
                  >
                    <MenuItem value="purple">Purple</MenuItem>
                    <MenuItem value="blue">Blue</MenuItem>
                    <MenuItem value="green">Green</MenuItem>
                    <MenuItem value="orange">Orange</MenuItem>
                    <MenuItem value="red">Red</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Card
              sx={{
                mt: 4,
                backgroundColor: "#f7f9fc",
                boxShadow: "none",
                borderRadius: 3,
                border: "1px solid #e5e7eb",
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 3,
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: 24,
                      fontWeight: 700,
                      color: "#6b7280",
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    CATALOG DISCOUNT
                  </span>
                  <span
                    style={{ fontSize: 40, fontWeight: 700, color: "green" }}
                  >
                    {tierDiscount}%
                  </span>
                </Box>

                <Slider
                  value={tierDiscount}
                  onChange={(e, newValue) => setTierDiscount(newValue)}
                  min={0}
                  max={100}
                  sx={{
                    color: "green",
                    height: 8,
                    "& .MuiSlider-thumb": {
                      width: 24,
                      height: 24,
                    },
                    "& .MuiSlider-track": {
                      height: 8,
                    },
                    "& .MuiSlider-rail": {
                      height: 8,
                      backgroundColor: "#e5e7eb",
                    },
                  }}
                />
                <p
                  style={{
                    fontSize: 14,
                    color: "#9ca3af",
                    marginTop: 16,
                    marginBottom: 0,
                  }}
                >
                  Applied automatically to MRP for all products.
                </p>
              </CardContent>
            </Card>

            {/* New Section: Payment Options */}
            <Box sx={{ mt: 4 }}>
              <Box sx={{ mb: 2 }}>
                <label
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#6b7280",
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  PAYMENT OPTIONS
                </label>
                <p
                  style={{
                    fontSize: 12,
                    color: "#9ca3af",
                    marginTop: 4,
                    marginBottom: 0,
                  }}
                >
                  Select at least one payment option
                </p>
              </Box>

              <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeCash}
                      onChange={(e) => setIncludeCash(e.target.checked)}
                      sx={{
                        color: "#0b4064",
                        "&.Mui-checked": {
                          color: "#0b4064",
                        },
                      }}
                    />
                  }
                  label={
                    <span style={{ fontSize: 16, fontWeight: 600 }}>Cash</span>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeCredit}
                      onChange={(e) => setIncludeCredit(e.target.checked)}
                      sx={{
                        color: "#0b4064",
                        "&.Mui-checked": {
                          color: "#0b4064",
                        },
                      }}
                    />
                  }
                  label={
                    <span style={{ fontSize: 16, fontWeight: 600 }}>
                      Credit
                    </span>
                  }
                />
              </Box>

              {/* Conditional Fields based on selection */}
              <Grid container spacing={3}>
                {/* Cash Discount Field - Show when Cash is selected */}
                {includeCash && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 1 }}>
                      <label
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#6b7280",
                          letterSpacing: 0.5,
                          textTransform: "uppercase",
                        }}
                      >
                        CASH DISCOUNT (%)
                      </label>
                    </Box>
                    <TextField
                      fullWidth
                      value={cashDiscount}
                      onChange={(e) => setCashDiscount(e.target.value)}
                      placeholder="Enter cash discount percentage"
                      type="number"
                      InputProps={{
                        inputProps: { min: 0, max: 100, step: 0.1 },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#f9fafb",
                        },
                      }}
                    />
                  </Grid>
                )}

                {/* Credit Fields - Show when Credit is selected */}
                {includeCredit && (
                  <>
                    <Grid item xs={12} md={includeCash ? 6 : 4}>
                      <Box sx={{ mb: 1 }}>
                        <label
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#6b7280",
                            letterSpacing: 0.5,
                            textTransform: "uppercase",
                          }}
                        >
                          CREDIT PERIOD (DAYS)
                        </label>
                      </Box>
                      <TextField
                        fullWidth
                        value={creditPeriod}
                        onChange={(e) => setCreditPeriod(e.target.value)}
                        placeholder="e.g., 30"
                        type="number"
                        InputProps={{ inputProps: { min: 1 } }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "#f9fafb",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={includeCash ? 6 : 4}>
                      <Box sx={{ mb: 1 }}>
                        <label
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#6b7280",
                            letterSpacing: 0.5,
                            textTransform: "uppercase",
                          }}
                        >
                          INTEREST RATE (%)
                        </label>
                      </Box>
                      <TextField
                        fullWidth
                        value={interestRate}
                        onChange={(e) => setInterestRate(e.target.value)}
                        placeholder="Annual interest rate"
                        type="number"
                        InputProps={{ inputProps: { min: 0, step: 0.1 } }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "#f9fafb",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={includeCash ? 6 : 4}>
                      <Box sx={{ mb: 1 }}>
                        <label
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#6b7280",
                            letterSpacing: 0.5,
                            textTransform: "uppercase",
                          }}
                        >
                          CREDIT LIMIT (₹)
                        </label>
                      </Box>
                      <TextField
                        fullWidth
                        value={creditLimit}
                        onChange={(e) => setCreditLimit(e.target.value)}
                        placeholder="Maximum credit amount"
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "#f9fafb",
                          },
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{ px: 4, py: 3, gap: 2, justifyContent: "flex-end" }}
          >
            <Button
              onClick={handleDialogClose}
              sx={{
                textTransform: "none",
                fontSize: 16,
                fontWeight: 600,
                color: "#6b7280",
                px: 3,
                py: 1.5,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveCategory}
              disabled={loading}
              sx={{
                backgroundColor: "#0b4064",
                textTransform: "none",
                px: 4,
                py: 1.5,
                fontSize: 16,
                fontWeight: 600,
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: "#0b4064",
                },
                "&:disabled": {
                  backgroundColor: "#cccccc",
                  color: "#666666",
                },
              }}
            >
              {loading ? "Saving..." : "Save Rules"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ---------------- Edit Buyer Category Dialog ---------------- */}
        <EditBuyerCategoryDialog
          open={openEditDialog}
          onClose={() => {
            setOpenEditDialog(false);
            setSelectedCategory(null);
            setSelectedPaymentOption(null);
          }}
          category={selectedCategory}
          paymentOption={selectedPaymentOption}
          onUpdate={handleUpdateCategory}
          loading={loading}
        />

        {/* ---------------- Delete Confirmation Dialog ---------------- */}
        <Dialog
          open={openDeleteDialog}
          onClose={handleDeleteDialogClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 },
          }}
        >
          <DialogTitle
            sx={{
              fontSize: 24,
              fontWeight: 700,
              pb: 2,
              pt: 3,
              px: 3,
              color: "#1a1a1a",
            }}
          >
            Delete Buyer Category
          </DialogTitle>
          <IconButton
            onClick={handleDeleteDialogClose}
            sx={{ position: "absolute", right: 16, top: 16, color: "#9ca3af" }}
          >
            <Close />
          </IconButton>

          <DialogContent sx={{ px: 3, pb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  backgroundColor: "#fee2e2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Delete sx={{ color: "#ef4444", fontSize: 20 }} />
              </Box>
              <Box>
                <h3
                  style={{
                    margin: 0,
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#1a1a1a",
                  }}
                >
                  Confirm Delete
                </h3>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: 14,
                    color: "#6b7280",
                  }}
                >
                  This action cannot be undone.
                </p>
              </Box>
            </Box>

            <Card
              sx={{
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 2,
                p: 2,
                mb: 2,
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  color: "#dc2626",
                  fontWeight: 600,
                }}
              >
                Are you sure you want to delete the buyer category "
                {categoryToDelete?.name}"?
              </p>
              <p
                style={{ margin: "8px 0 0 0", fontSize: 13, color: "#7c2d12" }}
              >
                This will also delete all associated payment options and
                settings.
              </p>
            </Card>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 2,
                backgroundColor: "#f9fafb",
                borderRadius: 2,
                mt: 2,
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  backgroundColor: "#fee2e2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span
                  style={{ fontSize: 12, fontWeight: 700, color: "#dc2626" }}
                >
                  !
                </span>
              </Box>
              <span style={{ fontSize: 13, color: "#6b7280" }}>
                All payment settings and rules associated with this category
                will be permanently removed.
              </span>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{ px: 3, py: 2, gap: 2, justifyContent: "flex-end" }}
          >
            <Button
              onClick={handleDeleteDialogClose}
              sx={{
                textTransform: "none",
                fontSize: 15,
                fontWeight: 600,
                color: "#6b7280",
                px: 3,
                py: 1,
                "&:hover": {
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmDelete}
              disabled={loading}
              sx={{
                backgroundColor: "#dc2626",
                textTransform: "none",
                px: 3,
                py: 1,
                fontSize: 15,
                fontWeight: 600,
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: "#b91c1c",
                },
                "&:disabled": {
                  backgroundColor: "#fca5a5",
                  color: "#ffffff",
                },
              }}
            >
              {loading ? "Deleting..." : "Delete Category"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default CategoryManagement;
